/**
 * EN↔ZH-Hant Language Pair Definition (Traditional Chinese)
 *
 * Taiwan / HK / Macau / overseas-繁體 ESL users. Derived from en-zh (simplified)
 * with the script flipped to Traditional. See .claude/plans/en-zh-hant.md.
 *
 * STEERING MECHANIC (important — buildProfile surfaces TARGET-side hints only):
 * buildProfile sets `profile.promptConfig.languageHints = target.promptConfig.languageHints`.
 * For the ESL forward direction (en-zh-hant, target=en) the zh-hant def's hints
 * would NEVER surface (en has none) — exactly why en-zh (simp) carries no hint
 * and relies on the model's simplified default. To steer Traditional on the
 * forward path we place a back-must-be-Traditional rule on this pair's OWN `en`
 * definition (the target), so buildProfile surfaces it into buildSystemPrompt.
 * The doc/media prompt builders don't inject languageHints — there, backLabel
 * ='Traditional Chinese' (from native.langName, in buildBaseSuffix) is the
 * steering signal. Drift is monitored by test #10; the OpenCC upgrade path
 * (plan §4.1) applies path-agnostically in intakeCard if drift > 10%.
 *
 * buildProfile() generates:
 *   enZhHant = ZH-Hant native → learn EN   (ESL validation direction)
 *   zhHantEn = EN native → learn ZH-Hant   (Direction A; registered for picker
 *              consistency + to avoid a silent fallback to en-zh — NOT promoted.
 *              Media reverse inherits the M1 English-source-only constraint.)
 */

import type { LanguagePairDefinition, LanguageProfile } from '../types'
import { buildProfile } from '../build-profile'
import { enZhPair } from './en-zh'

const zh = enZhPair.languages.zh

// Forward (ESL, target=en): minimal rule — the Chinese back must be Traditional.
// Placed on this pair's `en` def because buildProfile surfaces TARGET hints.
const FORWARD_TRAD_HINT =
  'The "back" translation and ANY Chinese text in the card MUST be Traditional Chinese characters (繁體字), never simplified. If a character has variants, always use the traditional form.'

// Reverse (target=zh-hant, learn Chinese): full target-side rules — measure
// words, pinyin tones, always-Traditional output, verb complements.
const ZH_HANT_HINTS = [
  'For nouns, always include the correct measure word (classifier) in the example sentence (e.g. 一隻貓, 一本書).',
  'Pinyin must include tone marks (e.g. mā, má, mǎ, mà). Do NOT use tone numbers (e.g. ma1 is wrong).',
  'ALWAYS output Traditional Chinese characters (繁體字). Never simplified. If a character has traditional/simplified variants, use the traditional form in "front", "back", "example", and "definition".',
  'For verbs, show the most common complement structure in the example.',
].join('\n')

const PAIR: LanguagePairDefinition = {
  id: 'en-zh-hant',
  languages: {
    // Pair-specific en def: identical to enZhPair.en except it carries the
    // forward Traditional-steering hint (buildProfile surfaces target hints).
    en: {
      ...enZhPair.languages.en,
      promptConfig: {
        ...enZhPair.languages.en.promptConfig,
        languageHints: FORWARD_TRAD_HINT,
      },
    },
    'zh-hant': {
      ...zh,
      lang: 'zh-hant',
      langName: 'Traditional Chinese',
      // Latin shortCode keeps the CN/JA/KO convention → screen-reader-friendly
      // direction label `EN→Hant`. (繁 as a shortCode would be the first non-Latin
      // one and read as an unintelligible glyph.)
      shortCode: 'Hant',
      // detectScript regex inherited — CJK range covers 繁體.
      // tts inherited (zh-CN voice); zh-TW voice deferred (plan §7).
      ui: {
        ...zh.ui,
        inputPlaceholder: '輸入單字、句子、或問題...',
        headingText: '今天想學什麼？',
        intentChips: [
          { label: '批量列表', intent: 'BATCH_LIST', suffix: '的英語怎麼說' },
          { label: '對比詞', intent: 'CONFUSABLE_PAIRS', type: 'compare', separator: 'vs' },
        ],
      },
      promptConfig: {
        ...zh.promptConfig,
        classifierHint: 'User may input in Chinese (simplified/traditional characters).',
        languageHints: ZH_HANT_HINTS,
      },
      // cardMeta inherited from zh — variants{simplified,traditional} still emitted.
    },
  },
}

export const enZhHantPair = PAIR
export const enZhHant: LanguageProfile = buildProfile(PAIR, 'en')
export const zhHantEn: LanguageProfile = buildProfile(PAIR, 'zh-hant')
